import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, Users, Building2, Store, BarChart3, LogOut, Menu, X,
  Euro, Plus, Download, TrendingUp, TrendingDown, Percent, FileText,
  Pencil, Search, KeyRound, Trash2, RefreshCw, ShoppingBag, Receipt,
  CheckCircle2, Clock, XCircle, Eye, Coins
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Logo from '../components/Logo';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [condominiums, setCondominiums] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dialog states
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newCondoOpen, setNewCondoOpen] = useState(false);
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', email: '', password: '', address: '', city: '', category: '', phone: '', description: '' });
  const [newCompanyResult, setNewCompanyResult] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'admin_condo' });
  const [newCondo, setNewCondo] = useState({ name: '', address: '', city: '', units_count: '' });
  const [companyRequests, setCompanyRequests] = useState([]);
  const [rejectReasonOpen, setRejectReasonOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalInfo, setApprovalInfo] = useState(null);

  // Admin power tools
  const [allTransactions, setAllTransactions] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [adminCondos, setAdminCondos] = useState([]);
  const [tempCredentials, setTempCredentials] = useState(null); // {email, password, label}
  const [editUser, setEditUser] = useState(null);
  const [editCondo, setEditCondo] = useState(null);
  const [editCompany, setEditCompany] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [txStatusFilter, setTxStatusFilter] = useState('all');
  const [txDaysFilter, setTxDaysFilter] = useState('30');
  const [txDetail, setTxDetail] = useState(null);
  const [newAdminCondoOpen, setNewAdminCondoOpen] = useState(false);
  const [newAdminCondo, setNewAdminCondo] = useState({ name: '', email: '', phone: '', condominium_id: '' });
  const [pointsConfig, setPointsConfig] = useState(null);


  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, condosRes, companiesRes, reqsRes, txRes, offersRes, adminCondosRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/condominiums'),
        api.get('/companies'),
        api.get('/admin/company-requests'),
        api.get(`/admin/transactions?days=${txDaysFilter}`).catch(() => ({ data: [] })),
        api.get('/admin/offers').catch(() => ({ data: [] })),
        api.get('/admin/admin-condos').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCondominiums(condosRes.data);
      setCompanies(companiesRes.data);
      setCompanyRequests(reqsRes.data);
      setAllTransactions(txRes.data);
      setAllOffers(offersRes.data);
      setAdminCondos(adminCondosRes.data);
      // Points config (Impostazioni)
      try {
        const pc = await api.get('/admin/settings/points');
        setPointsConfig(pc.data);
      } catch (_) {}
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [txDaysFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reload only transactions when filter changes
  useEffect(() => {
    if (loading) return;
    api.get(`/admin/transactions?days=${txDaysFilter}`)
      .then((r) => setAllTransactions(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txDaysFilter]);

  // ===== User actions =====
  const handleResetUserPassword = async (userId, label) => {
    if (!window.confirm('Generare una nuova password per questo account?')) return;
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      setTempCredentials({ email: res.data.email, password: res.data.temporary_password, label: label || 'Utente' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore reset password');
    }
  };
  const handleToggleUserActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/toggle-active`);
      toast.success(u.is_active ? 'Utente disattivato' : 'Utente attivato');
      fetchData();
    } catch (e) { toast.error('Errore'); }
  };
  const handleSaveUser = async () => {
    if (!editUser) return;
    try {
      await api.patch(`/admin/users/${editUser.id}`, {
        name: editUser.name, email: editUser.email, phone: editUser.phone || null,
        condominium_id: editUser.condominium_id || null, approval_status: editUser.approval_status,
      });
      toast.success('Utente aggiornato');
      setEditUser(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // ===== Condominium actions =====
  const handleSaveCondo = async () => {
    if (!editCondo) return;
    try {
      await api.patch(`/admin/condominiums/${editCondo.id}`, {
        name: editCondo.name, address: editCondo.address, city: editCondo.city,
        units_count: parseInt(editCondo.units_count) || 0,
      });
      toast.success('Condominio aggiornato');
      setEditCondo(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleDeleteCondo = async (condo) => {
    if (!window.confirm(`Disattivare "${condo.name}"?`)) return;
    try {
      await api.delete(`/admin/condominiums/${condo.id}`);
      toast.success('Condominio disattivato');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // ===== Company actions =====
  const handleSaveCompany = async () => {
    if (!editCompany) return;
    try {
      await api.patch(`/admin/companies/${editCompany.id}`, {
        name: editCompany.name, email: editCompany.email, phone: editCompany.phone || null,
        address: editCompany.address || null, city: editCompany.city || null,
        category: editCompany.category || null, vat_number: editCompany.vat_number || null,
        referent_name: editCompany.referent_name || null,
        is_active: editCompany.is_active, registration_paid: editCompany.registration_paid,
        wallet_balance: editCompany.wallet_balance != null ? parseFloat(editCompany.wallet_balance) : null,
      });
      toast.success('Azienda aggiornata');
      setEditCompany(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleResetCompanyPassword = async (company) => {
    if (!window.confirm('Generare una nuova password per questa azienda?')) return;
    try {
      const res = await api.post(`/admin/companies/${company.id}/reset-password`);
      setTempCredentials({ email: res.data.email, password: res.data.temporary_password, label: `Azienda: ${company.name}` });
    } catch (e) { toast.error('Errore'); }
  };

  // ===== Admin condo creation =====
  const handleCreateAdminCondo = async () => {
    if (!newAdminCondo.name || !newAdminCondo.email) {
      toast.error('Nome e email obbligatori'); return;
    }
    try {
      const res = await api.post('/admin/admin-condos', {
        name: newAdminCondo.name, email: newAdminCondo.email,
        phone: newAdminCondo.phone || null,
        condominium_id: newAdminCondo.condominium_id || null,
      });
      setNewAdminCondoOpen(false);
      setNewAdminCondo({ name: '', email: '', phone: '', condominium_id: '' });
      setTempCredentials({ email: res.data.email, password: res.data.temporary_password, label: 'Nuovo Admin Condominio' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione'); }
  };

  // ===== Transaction actions =====
  const handleForceConfirmTx = async (tx) => {
    if (!window.confirm('Forzare conferma di questa transazione?')) return;
    try {
      await api.post(`/admin/transactions/${tx.id}/force-confirm`);
      toast.success('Transazione confermata');
      setTxDetail(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleCancelTx = async (tx) => {
    if (!window.confirm('Annullare questa transazione?')) return;
    try {
      await api.post(`/admin/transactions/${tx.id}/cancel`);
      toast.success('Transazione annullata');
      setTxDetail(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // ===== Offer actions =====
  const handleToggleOffer = async (offer) => {
    const newStatus = offer.status === 'active' ? 'suspended' : 'active';
    try {
      await api.put(`/offers/${offer.id}/status?status=${newStatus}`);
      toast.success(newStatus === 'active' ? 'Proposta riattivata' : 'Proposta sospesa');
      fetchData();
    } catch (e) { toast.error('Errore'); }
  };
  const handleDeleteOffer = async (offer) => {
    if (!window.confirm(`Eliminare l'proposta "${offer.title}"?`)) return;
    try {
      await api.delete(`/admin/offers/${offer.id}`);
      toast.success('Proposta eliminata');
      fetchData();
    } catch (e) { toast.error('Errore'); }
  };

  // Filter helpers
  const filteredUsers = users.filter((u) => {
    if (userRoleFilter !== 'all' && u.role !== userRoleFilter) return false;
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      if (!(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const filteredTransactions = allTransactions.filter((t) => {
    if (txStatusFilter !== 'all' && t.status !== txStatusFilter) return false;
    return true;
  });

  const fetchPointsConfig = async () => {
    try {
      const r = await api.get('/admin/settings/points');
      setPointsConfig(r.data);
    } catch (e) { toast.error('Errore caricamento impostazioni'); }
  };

  const handleSavePointsConfig = async () => {
    if (!pointsConfig) return;
    const m = parseFloat(pointsConfig.merchant_point_value);
    const u = parseFloat(pointsConfig.user_point_value);
    if (isNaN(m) || isNaN(u) || m < 0 || u < 0) { toast.error('Valori non validi'); return; }
    if (u > m) { toast.error("Il valore per l'utente non può superare quello del commerciante"); return; }
    try {
      const res = await api.put('/admin/settings/points', { merchant_point_value: m, user_point_value: u });
      setPointsConfig(res.data);
      toast.success('Impostazioni salvate');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleApproveRequest = async (reqId, skipPayment) => {
    try {
      const res = await api.post(`/admin/company-requests/${reqId}/approve?skip_payment=${skipPayment}`);
      setApprovalInfo(res.data);
      toast.success('Azienda approvata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    }
  };

  const handleRejectRequest = async (reqId) => {
    try {
      await api.post(`/admin/company-requests/${reqId}/reject`, null, { params: { reason: rejectReason } });
      toast.success('Richiesta rifiutata');
      setRejectReasonOpen(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    }
  };

  const handleCreateUser = async () => {
    try {
      await api.post(`/admin/users?role=${newUser.role}`, newUser);
      toast.success('Utente creato con successo!');
      setNewUserOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'admin_condo' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleCreateCondo = async () => {
    try {
      await api.post('/condominiums', {
        ...newCondo,
        units_count: parseInt(newCondo.units_count)
      });
      toast.success('Condominio creato con successo!');
      setNewCondoOpen(false);
      setNewCondo({ name: '', address: '', city: '', units_count: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleCreateCompany = async () => {
    // Basic validation
    const required = ['name', 'email', 'password', 'address', 'city', 'category'];
    for (const f of required) {
      if (!newCompany[f] || !String(newCompany[f]).trim()) {
        toast.error(`Compila il campo "${f}"`);
        return;
      }
    }
    if (newCompany.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    try {
      const res = await api.post('/companies', newCompany);
      // Mark as registration paid + active by default (admin created bypasses payment)
      try {
        await api.patch(`/admin/companies/${res.data.id}`, { registration_paid: true, is_active: true });
      } catch (e) {}
      setNewCompanyResult({ email: newCompany.email, password: newCompany.password, id: res.data.id });
      toast.success('Azienda creata con successo');
      setNewCompanyOpen(false);
      setNewCompany({ name: '', email: '', password: '', address: '', city: '', category: '', phone: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione azienda');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const COLORS = ['#0F172A', '#F59E0B', '#10B981'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const commissionData = [
    { name: 'Utenti', value: stats?.total_users || 0, color: '#0F172A' },
    { name: 'Aziende', value: stats?.total_companies || 0, color: '#F59E0B' },
    { name: 'Condomini', value: stats?.total_condominiums || 0, color: '#10B981' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-lg p-1">
                <Logo linkTo="/" size="sm" />
              </div>
              <div className="border-l border-slate-700 pl-3">
                <h1 className="font-semibold">Admin AIAC</h1>
                <p className="text-xs text-slate-400">Pannello di Controllo</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" className="text-white hover:bg-slate-800" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Utenti Totali</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Aziende</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_companies || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Store className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Condomini</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_condominiums || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Punti Totali</p>
                  <p className="text-2xl font-bold text-amber-600">{stats?.total_commissions_earned?.toFixed(0) || '0'}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 w-full sm:w-auto justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Utenti</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">Transazioni</TabsTrigger>
            <TabsTrigger value="condominiums" data-testid="condos-tab">Condomini</TabsTrigger>
            <TabsTrigger value="admin-condos" data-testid="admin-condos-tab">Admin Condo</TabsTrigger>
            <TabsTrigger value="companies" data-testid="companies-tab">Aziende</TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab">Proposte</TabsTrigger>
            <TabsTrigger value="company-requests" data-testid="company-requests-tab">
              Richieste{companyRequests.filter(r => r.status === 'pending').length > 0 && ` (${companyRequests.filter(r => r.status === 'pending').length})`}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Impostazioni</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuzione Piattaforma</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={commissionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {commissionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    {commissionData.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                        <span className="text-sm text-slate-600">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Statistiche Punti</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                      <div>
                        <p className="text-sm text-amber-600">Punti Distribuiti</p>
                        <p className="text-2xl font-bold text-amber-700">{stats?.total_cashback_distributed?.toFixed(0) || '0'}</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-amber-500" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div>
                        <p className="text-sm text-blue-600">Transazioni Completate</p>
                        <p className="text-2xl font-bold text-blue-700">{stats?.total_transactions || 0}</p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                      <div>
                        <p className="text-sm text-emerald-600">Proposte Attive</p>
                        <p className="text-2xl font-bold text-emerald-700">{stats?.active_offers || 0}</p>
                      </div>
                      <Percent className="w-8 h-8 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle>Gestione Utenti ({filteredUsers.length})</CardTitle>
                  <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="new-user-btn"><Plus className="w-4 h-4 mr-2" />Nuovo Utente</Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Crea Nuovo Utente</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Ruolo</Label>
                          <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin Generale</SelectItem>
                              <SelectItem value="admin_condo">Admin Condominiale</SelectItem>
                              <SelectItem value="commercial">Commerciale</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label>Nome</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></div>
                        <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></div>
                        <div><Label>Password</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></div>
                        <Button className="w-full" onClick={handleCreateUser}>Crea Utente</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Cerca per nome o email..." value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)} className="pl-9" data-testid="user-search-input" />
                  </div>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="user-role-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i ruoli</SelectItem>
                      <SelectItem value="admin">Admin Generale</SelectItem>
                      <SelectItem value="admin_condo">Admin Condo</SelectItem>
                      <SelectItem value="company">Aziende</SelectItem>
                      <SelectItem value="user">Utenti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {filteredUsers.map((u) => (
                    <div key={u.id} className="p-3 border border-slate-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`admin-user-row-${u.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold truncate">{u.name}</h4>
                          <Badge className={
                            u.role === 'admin' ? 'bg-slate-900 text-white' :
                            u.role === 'admin_condo' ? 'bg-emerald-100 text-emerald-700' :
                            u.role === 'commercial' ? 'bg-amber-100 text-amber-700' :
                            u.role === 'company' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {u.role === 'admin' ? 'Admin' : u.role === 'admin_condo' ? 'Admin Condo' :
                             u.role === 'commercial' ? 'Commerciale' : u.role === 'company' ? 'Azienda' : 'Utente'}
                          </Badge>
                          {!u.is_active && <Badge className="bg-red-100 text-red-700">Disattivato</Badge>}
                          {u.approval_status === 'pending' && <Badge className="bg-amber-200 text-amber-800">In attesa</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{u.email} {u.phone && `· ${u.phone}`}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => setEditUser({ ...u })} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleResetUserPassword(u.id, u.name)} data-testid={`reset-pwd-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleUserActive(u)} className={u.is_active ? 'border-red-300 text-red-700' : 'border-emerald-300 text-emerald-700'} data-testid={`toggle-active-${u.id}`}>
                          {u.is_active ? <X className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && <p className="text-center py-8 text-slate-500">Nessun utente trovato</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transazioni globali ({filteredTransactions.length})</CardTitle>
                <div className="flex flex-col sm:flex-row gap-3 mt-3">
                  <Select value={txDaysFilter} onValueChange={setTxDaysFilter}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="admin-tx-days-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Oggi</SelectItem>
                      <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                      <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                      <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                      <SelectItem value="365">Ultimo anno</SelectItem>
                      <SelectItem value="0">Tutte</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={txStatusFilter} onValueChange={setTxStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="admin-tx-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli stati</SelectItem>
                      <SelectItem value="pending">In attesa</SelectItem>
                      <SelectItem value="confirmed">Confermate</SelectItem>
                      <SelectItem value="rejected">Rifiutate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {filteredTransactions.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">Nessuna transazione</p>
                ) : (
                  <div className="space-y-2">
                    {filteredTransactions.map((tx) => (
                      <button key={tx.id} type="button" onClick={() => setTxDetail(tx)}
                        className="w-full text-left p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`admin-tx-row-${tx.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{tx.user_name} → {tx.company_name}</p>
                            <p className="text-sm text-slate-500 truncate">{tx.offer_title}</p>
                            <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleString('it-IT')}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold">€{tx.amount_spent.toFixed(2)}</p>
                            <p className="text-xs text-emerald-600">+{tx.cashback_net.toFixed(0)} pt</p>
                            <Badge className={
                              tx.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                              tx.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }>
                              {tx.status === 'confirmed' ? 'Confermata' : tx.status === 'pending' ? 'In attesa' : 'Rifiutata'}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Condominiums Tab */}
          <TabsContent value="condominiums">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gestione Condomini</CardTitle>
                <Dialog open={newCondoOpen} onOpenChange={setNewCondoOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="new-condo-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuovo Condominio
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crea Nuovo Condominio</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Nome</Label>
                        <Input
                          value={newCondo.name}
                          onChange={(e) => setNewCondo({ ...newCondo, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Indirizzo</Label>
                        <Input
                          value={newCondo.address}
                          onChange={(e) => setNewCondo({ ...newCondo, address: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Città</Label>
                        <Input
                          value={newCondo.city}
                          onChange={(e) => setNewCondo({ ...newCondo, city: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Numero Unità</Label>
                        <Input
                          type="number"
                          value={newCondo.units_count}
                          onChange={(e) => setNewCondo({ ...newCondo, units_count: e.target.value })}
                        />
                      </div>
                      <Button className="w-full" onClick={handleCreateCondo}>
                        Crea Condominio
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {condominiums.map((c) => (
                    <div key={c.id} className="p-3 border border-slate-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`admin-condo-row-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold truncate">{c.name}</h4>
                        <p className="text-sm text-slate-500 truncate">{c.address}{c.city ? `, ${c.city}` : ''}</p>
                        <p className="text-xs text-slate-400">{c.units_count} unità · Commissioni €{c.total_commission?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setEditCondo({ ...c })} data-testid={`edit-condo-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => handleDeleteCondo(c)} data-testid={`delete-condo-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Condomini Tab */}
          <TabsContent value="admin-condos">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle>Admin Condomini ({adminCondos.length})</CardTitle>
                  <Button onClick={() => setNewAdminCondoOpen(true)} data-testid="new-admin-condo-btn">
                    <Plus className="w-4 h-4 mr-2" />Nuovo Admin Condo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {adminCondos.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">Nessun admin condominiale</p>
                ) : (
                  <div className="space-y-3">
                    {adminCondos.map((a) => (
                      <div key={a.id} className="p-3 border border-slate-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`admin-condo-account-${a.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold truncate">{a.name}</h4>
                            {!a.is_active && <Badge className="bg-red-100 text-red-700">Disattivato</Badge>}
                          </div>
                          <p className="text-sm text-slate-500 truncate">{a.email} {a.phone && `· ${a.phone}`}</p>
                          {a.condominium ? (
                            <p className="text-xs text-emerald-700 mt-1">
                              <Building2 className="w-3 h-3 inline mr-1" />
                              {a.condominium.name} · {a.condominium.units_count} unità
                            </p>
                          ) : (
                            <p className="text-xs text-amber-600 mt-1">Nessun condominio assegnato</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => setEditUser({ ...a })}><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleResetUserPassword(a.id, a.name)}><KeyRound className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleToggleUserActive(a)} className={a.is_active ? 'border-red-300 text-red-700' : 'border-emerald-300 text-emerald-700'}>
                            {a.is_active ? <X className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Aziende ({companies.length})</CardTitle>
                <Button
                  onClick={() => setNewCompanyOpen(true)}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="new-company-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nuova Azienda
                </Button>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna azienda registrata</p>
                ) : (
                  <div className="space-y-3">
                    {companies.map((c) => (
                      <div key={c.id} className="p-3 border border-slate-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`admin-company-row-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold truncate">{c.name}</h4>
                            {!c.is_active && <Badge className="bg-red-100 text-red-700">Disattivata</Badge>}
                            <Badge className={c.registration_paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {c.registration_paid ? 'Attiva' : 'In attesa pagamento'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 truncate">{c.email} {c.phone && `· ${c.phone}`}</p>
                          <p className="text-xs text-slate-400 truncate">
                            {c.category}{c.city ? ` · ${c.city}` : ''}{c.vat_number ? ` · P.IVA ${c.vat_number}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 flex-wrap">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Borsellino</p>
                            <p className="font-bold">€{(c.wallet_balance || 0).toFixed(2)}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => setEditCompany({ ...c })} data-testid={`edit-company-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleResetCompanyPassword(c)} data-testid={`reset-pwd-company-${c.id}`}><KeyRound className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* New Company Dialog (admin) */}
            <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-company-dialog">
                <DialogHeader>
                  <DialogTitle>Nuova Azienda Partner</DialogTitle>
                  <DialogDescription>
                    Crea manualmente un'azienda partner. Verrà marcata come attiva e con iscrizione già pagata.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nome azienda *</Label>
                    <Input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} placeholder="Es. Ristorante Da Mario" data-testid="new-co-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Email login *</Label>
                      <Input type="email" value={newCompany.email} onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })} placeholder="info@azienda.it" data-testid="new-co-email" />
                    </div>
                    <div>
                      <Label>Password temporanea *</Label>
                      <Input type="text" value={newCompany.password} onChange={(e) => setNewCompany({ ...newCompany, password: e.target.value })} placeholder="min 6 caratteri" data-testid="new-co-password" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Città *</Label>
                      <Input value={newCompany.city} onChange={(e) => setNewCompany({ ...newCompany, city: e.target.value })} placeholder="Roma" data-testid="new-co-city" />
                    </div>
                    <div>
                      <Label>Categoria *</Label>
                      <select
                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                        value={newCompany.category}
                        onChange={(e) => setNewCompany({ ...newCompany, category: e.target.value })}
                        data-testid="new-co-category"
                      >
                        <option value="">Seleziona…</option>
                        <option value="Ristorazione">Ristorazione</option>
                        <option value="Servizi">Servizi</option>
                        <option value="Shopping">Shopping</option>
                        <option value="Salute e Benessere">Salute e Benessere</option>
                        <option value="Casa e Giardino">Casa e Giardino</option>
                        <option value="Tempo Libero">Tempo Libero</option>
                        <option value="Auto e Moto">Auto e Moto</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Indirizzo *</Label>
                    <Input value={newCompany.address} onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })} placeholder="Via del Corso 123" data-testid="new-co-address" />
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input value={newCompany.phone} onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })} placeholder="06 12345678" data-testid="new-co-phone" />
                  </div>
                  <div>
                    <Label>Descrizione</Label>
                    <textarea
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                      rows={3}
                      value={newCompany.description}
                      onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                      placeholder="Breve descrizione dell'attività"
                      data-testid="new-co-description"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setNewCompanyOpen(false)}>Annulla</Button>
                    <Button onClick={handleCreateCompany} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-company-btn">Crea Azienda</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Show generated credentials after creation */}
            <Dialog open={!!newCompanyResult} onOpenChange={(o) => { if (!o) setNewCompanyResult(null); }}>
              <DialogContent className="max-w-md" data-testid="new-company-result-dialog">
                <DialogHeader>
                  <DialogTitle>Azienda creata ✅</DialogTitle>
                  <DialogDescription>
                    Copia le credenziali e comunicale al partner. Non saranno più visibili.
                  </DialogDescription>
                </DialogHeader>
                {newCompanyResult && (
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Email login</p>
                      <p className="font-mono font-semibold" data-testid="result-co-email">{newCompanyResult.email}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Password</p>
                      <p className="font-mono font-semibold" data-testid="result-co-password">{newCompanyResult.password}</p>
                    </div>
                    <p className="text-xs text-amber-700">Suggerisci al partner di cambiarla al primo login.</p>
                    <div className="flex justify-end">
                      <Button onClick={() => setNewCompanyResult(null)}>Ho copiato</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader><CardTitle>Tutte le proposte ({allOffers.length})</CardTitle></CardHeader>
              <CardContent>
                {allOffers.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">Nessuna proposta</p>
                ) : (
                  <div className="space-y-3">
                    {allOffers.map((o) => (
                      <div key={o.id} className="p-3 border border-slate-200 rounded-lg flex items-center justify-between gap-3" data-testid={`admin-offer-row-${o.id}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {o.image_url && <img src={o.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold truncate">{o.title}</h4>
                              <Badge className={
                                o.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                o.status === 'suspended' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                              }>
                                {o.status === 'active' ? 'Attiva' : o.status === 'suspended' ? 'Sospesa' : 'Scaduta'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 truncate">{o.company_name} · {o.category} · {Math.round(o.points_awarded ?? o.discount_percentage ?? 0)} pt / €{Math.round(o.base_price ?? 100)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => handleToggleOffer(o)} data-testid={`toggle-offer-${o.id}`}>
                            {o.status === 'active' ? 'Sospendi' : 'Attiva'}
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => handleDeleteOffer(o)} data-testid={`delete-offer-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Registration Requests Tab */}
          <TabsContent value="company-requests">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-amber-600" />
                  Richieste di Iscrizione Aziende ({companyRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {companyRequests.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna richiesta ricevuta</p>
                ) : (
                  <div className="space-y-3">
                    {companyRequests.map((req) => (
                      <div key={req.id} className={`p-4 border rounded-xl ${req.status === 'pending' ? 'border-amber-200 bg-amber-50' : req.status === 'approved' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{req.company_name}</h4>
                              <Badge className={req.status === 'pending' ? 'bg-amber-200 text-amber-800' : req.status === 'approved' ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-700'}>
                                {req.status === 'pending' ? 'In attesa' : req.status === 'approved' ? 'Approvata' : 'Rifiutata'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-700">Referente: <span className="font-medium">{req.referent_name}</span></p>
                            <p className="text-sm text-slate-600">{req.email} · {req.phone}</p>
                            <p className="text-xs text-slate-500 font-mono">P.IVA: {req.vat_number}</p>
                            {(req.city || req.category) && (
                              <p className="text-xs text-slate-500 mt-1">{req.category} {req.city ? `· ${req.city}` : ''}</p>
                            )}
                            {req.description && <p className="text-xs text-slate-600 mt-2 italic">"{req.description}"</p>}
                            <p className="text-xs text-slate-400 mt-2">
                              Ricevuta: {new Date(req.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          {req.status === 'pending' && (
                            <div className="flex flex-col gap-2 shrink-0">
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveRequest(req.id, false)}
                                data-testid={`approve-req-${req.id}`}
                              >
                                Approva (richiedi pag. 250€)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-emerald-400 text-emerald-700"
                                onClick={() => handleApproveRequest(req.id, true)}
                                data-testid={`approve-skip-${req.id}`}
                              >
                                Approva & salta pag.
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700"
                                onClick={() => setRejectReasonOpen(req.id)}
                                data-testid={`reject-req-${req.id}`}
                              >
                                Rifiuta
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Plan Tab */}
          {/* Settings Tab (Punti config) */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-amber-600" />
                  Configurazione Punti
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Definisci il valore di 1 punto per il commerciante (costo) e per l'utente finale (valore in crediti condominiali). La differenza resta alla piattaforma.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {pointsConfig && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <Label className="text-xs text-slate-500">Valore per Commerciante (€/pt)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={pointsConfig.merchant_point_value}
                          onChange={(e) => setPointsConfig({ ...pointsConfig, merchant_point_value: e.target.value })}
                          className="mt-1 text-lg font-semibold"
                          data-testid="merchant-point-value-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">Quanto viene scalato dal borsellino del commerciante per ogni punto emesso</p>
                      </div>
                      <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                        <Label className="text-xs text-emerald-700">Valore per Utente (€/pt)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={pointsConfig.user_point_value}
                          onChange={(e) => setPointsConfig({ ...pointsConfig, user_point_value: e.target.value })}
                          className="mt-1 text-lg font-semibold"
                          data-testid="user-point-value-input"
                        />
                        <p className="text-xs text-emerald-700 mt-1">Valore in crediti condominiali che l'utente riceve per ogni punto</p>
                      </div>
                      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <Label className="text-xs text-amber-700">Delta Piattaforma (€/pt)</Label>
                        <p className="mt-1 text-2xl font-bold text-amber-800">
                          €{Math.max(0, (parseFloat(pointsConfig.merchant_point_value) || 0) - (parseFloat(pointsConfig.user_point_value) || 0)).toFixed(3)}
                        </p>
                        <p className="text-xs text-amber-700 mt-2">Margine per la piattaforma su ogni punto emesso</p>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 rounded-xl bg-slate-900 text-white">
                      <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Esempio di transazione</p>
                      <p className="text-sm">
                        Cliente spende <strong className="text-amber-400">€50</strong> su una proposta da <strong className="text-amber-400">10 punti / €50</strong>:
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div><p className="text-xs text-slate-400">Punti generati</p><p className="font-bold">10 pt</p></div>
                        <div><p className="text-xs text-slate-400">Costo commerciante</p><p className="font-bold text-red-300">€{(10 * (parseFloat(pointsConfig.merchant_point_value) || 0)).toFixed(2)}</p></div>
                        <div><p className="text-xs text-slate-400">Crediti utente</p><p className="font-bold text-emerald-300">€{(10 * (parseFloat(pointsConfig.user_point_value) || 0)).toFixed(2)}</p></div>
                        <div><p className="text-xs text-slate-400">Piattaforma</p><p className="font-bold text-amber-300">€{Math.max(0, (10 * ((parseFloat(pointsConfig.merchant_point_value) || 0) - (parseFloat(pointsConfig.user_point_value) || 0)))).toFixed(2)}</p></div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={fetchPointsConfig} data-testid="reset-points-config-btn">
                        <RefreshCw className="w-4 h-4 mr-1" /> Ripristina
                      </Button>
                      <Button onClick={handleSavePointsConfig} data-testid="save-points-config-btn">
                        Salva impostazioni
                      </Button>
                    </div>

                    {pointsConfig.updated_at && (
                      <p className="text-xs text-slate-400">
                        Ultimo aggiornamento: {new Date(pointsConfig.updated_at).toLocaleString('it-IT')}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reject Reason Dialog */}
      <Dialog open={!!rejectReasonOpen} onOpenChange={(o) => { if (!o) { setRejectReasonOpen(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Motivo del rifiuto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Es: dati non conformi"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setRejectReasonOpen(null); setRejectReason(''); }}>
                Annulla
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => handleRejectRequest(rejectReasonOpen)} data-testid="confirm-reject-btn">
                Rifiuta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Success Dialog */}
      <Dialog open={!!approvalInfo} onOpenChange={(o) => { if (!o) setApprovalInfo(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Azienda approvata</DialogTitle>
          </DialogHeader>
          {approvalInfo && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">Credenziali generate per l'azienda:</p>
              <div className="p-3 rounded-lg bg-slate-100 font-mono text-sm">
                <p><span className="text-slate-500">Email: </span>{approvalInfo.email}</p>
                <p><span className="text-slate-500">Password temporanea: </span><strong>{approvalInfo.temporary_password}</strong></p>
                <p><span className="text-slate-500">Iscrizione 250€: </span>{approvalInfo.registration_paid ? 'Saltata (attivata)' : 'Da saldare al primo login'}</p>
              </div>
              <p className="text-xs text-slate-500">
                Invia queste credenziali al referente via email (integrazione email non ancora attiva).
              </p>
              <Button className="w-full" onClick={() => {
                navigator.clipboard.writeText(`Email: ${approvalInfo.email}\nPassword: ${approvalInfo.temporary_password}`);
                toast.success('Credenziali copiate');
              }}>
                Copia credenziali
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temporary Credentials Dialog (reset password / new admin condo) */}
      <Dialog open={!!tempCredentials} onOpenChange={(o) => { if (!o) setTempCredentials(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Credenziali generate</DialogTitle>
          </DialogHeader>
          {tempCredentials && (
            <div className="space-y-3">
              {tempCredentials.label && <p className="text-sm font-medium">{tempCredentials.label}</p>}
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ Salva queste credenziali ora: non saranno mostrate di nuovo
              </p>
              <div className="p-3 rounded-lg bg-slate-100 font-mono text-sm" data-testid="temp-credentials-box">
                <p><span className="text-slate-500">Email: </span>{tempCredentials.email}</p>
                <p><span className="text-slate-500">Password: </span><strong>{tempCredentials.password}</strong></p>
              </div>
              <Button className="w-full" onClick={() => {
                navigator.clipboard.writeText(`Email: ${tempCredentials.email}\nPassword: ${tempCredentials.password}`);
                toast.success('Copiate negli appunti');
              }}>Copia credenziali</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Utente</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editUser.name || ''} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editUser.email || ''} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email" /></div>
              <div><Label>Telefono</Label><Input value={editUser.phone || ''} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} /></div>
              {(editUser.role === 'admin_condo' || editUser.role === 'user') && (
                <div>
                  <Label>Condominio</Label>
                  <Select value={editUser.condominium_id || ''} onValueChange={(v) => setEditUser({ ...editUser, condominium_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleziona condominio" /></SelectTrigger>
                    <SelectContent>
                      {condominiums.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editUser.role === 'user' && (
                <div>
                  <Label>Stato approvazione</Label>
                  <Select value={editUser.approval_status || 'approved'} onValueChange={(v) => setEditUser({ ...editUser, approval_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approvato</SelectItem>
                      <SelectItem value="pending">In attesa</SelectItem>
                      <SelectItem value="rejected">Rifiutato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditUser(null)}>Annulla</Button>
                <Button className="flex-1" onClick={handleSaveUser} data-testid="save-user-btn">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Condominium Dialog */}
      <Dialog open={!!editCondo} onOpenChange={(o) => { if (!o) setEditCondo(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Condominio</DialogTitle></DialogHeader>
          {editCondo && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editCondo.name || ''} onChange={(e) => setEditCondo({ ...editCondo, name: e.target.value })} data-testid="edit-condo-name" /></div>
              <div><Label>Indirizzo</Label><Input value={editCondo.address || ''} onChange={(e) => setEditCondo({ ...editCondo, address: e.target.value })} /></div>
              <div><Label>Città</Label><Input value={editCondo.city || ''} onChange={(e) => setEditCondo({ ...editCondo, city: e.target.value })} /></div>
              <div><Label>Numero unità</Label><Input type="number" value={editCondo.units_count || 0} onChange={(e) => setEditCondo({ ...editCondo, units_count: e.target.value })} /></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditCondo(null)}>Annulla</Button>
                <Button className="flex-1" onClick={handleSaveCondo} data-testid="save-condo-btn">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={!!editCompany} onOpenChange={(o) => { if (!o) setEditCompany(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Azienda</DialogTitle></DialogHeader>
          {editCompany && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ragione sociale</Label><Input value={editCompany.name || ''} onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })} data-testid="edit-company-name" /></div>
                <div><Label>P.IVA</Label><Input value={editCompany.vat_number || ''} onChange={(e) => setEditCompany({ ...editCompany, vat_number: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={editCompany.email || ''} onChange={(e) => setEditCompany({ ...editCompany, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={editCompany.phone || ''} onChange={(e) => setEditCompany({ ...editCompany, phone: e.target.value })} /></div>
              </div>
              <div><Label>Referente</Label><Input value={editCompany.referent_name || ''} onChange={(e) => setEditCompany({ ...editCompany, referent_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Indirizzo</Label><Input value={editCompany.address || ''} onChange={(e) => setEditCompany({ ...editCompany, address: e.target.value })} /></div>
                <div><Label>Città</Label><Input value={editCompany.city || ''} onChange={(e) => setEditCompany({ ...editCompany, city: e.target.value })} /></div>
              </div>
              <div><Label>Categoria</Label><Input value={editCompany.category || ''} onChange={(e) => setEditCompany({ ...editCompany, category: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Borsellino (€)</Label>
                  <Input type="number" step="0.01" value={editCompany.wallet_balance ?? 0} onChange={(e) => setEditCompany({ ...editCompany, wallet_balance: e.target.value })} />
                </div>
                <div>
                  <Label>Iscrizione 250€ pagata</Label>
                  <Select value={editCompany.registration_paid ? 'yes' : 'no'} onValueChange={(v) => setEditCompany({ ...editCompany, registration_paid: v === 'yes' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Sì</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editCompany.is_active ? 'active' : 'inactive'} onValueChange={(v) => setEditCompany({ ...editCompany, is_active: v === 'active' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Attiva</SelectItem>
                    <SelectItem value="inactive">Disattivata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditCompany(null)}>Annulla</Button>
                <Button className="flex-1" onClick={handleSaveCompany} data-testid="save-company-btn">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Admin Condo Dialog */}
      <Dialog open={newAdminCondoOpen} onOpenChange={setNewAdminCondoOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Admin Condominio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome completo *</Label><Input value={newAdminCondo.name} onChange={(e) => setNewAdminCondo({ ...newAdminCondo, name: e.target.value })} data-testid="new-ac-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={newAdminCondo.email} onChange={(e) => setNewAdminCondo({ ...newAdminCondo, email: e.target.value })} data-testid="new-ac-email" /></div>
            <div><Label>Telefono</Label><Input value={newAdminCondo.phone} onChange={(e) => setNewAdminCondo({ ...newAdminCondo, phone: e.target.value })} /></div>
            <div>
              <Label>Condominio (opzionale)</Label>
              <Select value={newAdminCondo.condominium_id} onValueChange={(v) => setNewAdminCondo({ ...newAdminCondo, condominium_id: v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  {condominiums.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              La password verrà generata automaticamente e mostrata una volta sola.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setNewAdminCondoOpen(false)}>Annulla</Button>
              <Button className="flex-1" onClick={handleCreateAdminCondo} data-testid="create-admin-condo-btn">Crea</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transaction Detail Dialog (admin) */}
      <Dialog open={!!txDetail} onOpenChange={(o) => { if (!o) setTxDetail(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dettaglio Transazione</DialogTitle></DialogHeader>
          {txDetail && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <Badge className={
                  txDetail.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                  txDetail.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }>
                  {txDetail.status === 'confirmed' ? 'Confermata' : txDetail.status === 'pending' ? 'In attesa' : 'Rifiutata'}
                </Badge>
                <p className="font-bold text-lg">€{txDetail.amount_spent.toFixed(2)}</p>
              </div>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">Cliente:</span> {txDetail.user_name}</p>
                <p><span className="text-slate-500">Azienda:</span> {txDetail.company_name}</p>
                <p><span className="text-slate-500">Proposta:</span> {txDetail.offer_title}</p>
                <p><span className="text-slate-500">Punti netti:</span> {txDetail.cashback_net.toFixed(0)}</p>
                <p><span className="text-slate-500">Creata il:</span> {new Date(txDetail.created_at).toLocaleString('it-IT')}</p>
                {txDetail.confirmed_at && <p><span className="text-slate-500">Confermata il:</span> {new Date(txDetail.confirmed_at).toLocaleString('it-IT')}</p>}
                <p className="font-mono text-xs"><span className="text-slate-500">ID:</span> {txDetail.id}</p>
              </div>
              {txDetail.status === 'pending' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" className="flex-1 border-red-300 text-red-700" onClick={() => handleCancelTx(txDetail)} data-testid="admin-cancel-tx-btn">
                    Annulla
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleForceConfirmTx(txDetail)} data-testid="admin-force-confirm-btn">
                    Forza conferma
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
